import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, AlertTriangle, Calendar, Clock, User, Phone } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import SEOHead from "@/components/SEOHead";

const API = process.env.REACT_APP_BACKEND_URL;

const timeSlots = [
  { time: "11:00", label: "2018-2019 Doğumlular" },
  { time: "12:00", label: "2016-2017 Doğumlular" },
  { time: "13:00", label: "2013-2014-2015 Doğumlular" },
  { time: "14:00", label: "2010-2011-2012 Doğumlular" },
];

const dayOptions = ["5 Eylül", "6 Eylül"];

export default function TrialRegistrationPage() {
  const [form, setForm] = useState({
    athlete_name: "",
    athlete_surname: "",
    birth_date: "",
    has_license: "",
    parent_name: "",
    parent_surname: "",
    parent_phone: "",
    trial_day: "",
    trial_time: "",
    consent: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.athlete_name.trim()) e.athlete_name = true;
    if (!form.athlete_surname.trim()) e.athlete_surname = true;
    if (!form.birth_date) e.birth_date = true;
    if (!form.has_license) e.has_license = true;
    if (!form.parent_name.trim()) e.parent_name = true;
    if (!form.parent_surname.trim()) e.parent_surname = true;
    if (!form.parent_phone.trim()) e.parent_phone = true;
    if (!form.trial_day) e.trial_day = true;
    if (!form.trial_time) e.trial_time = true;
    if (!form.consent) e.consent = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Lütfen tüm alanları doldurun.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/trial-registration`, form);
      toast.success("Deneme antrenman kaydınız başarıyla alındı!");
      setSubmitted(true);
    } catch {
      toast.error("Kayıt sırasında bir hata oluştu. Lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: false }));
  };

  if (submitted) {
    return (
      <div className="pt-16 lg:pt-20 min-h-screen bg-[#1A1A1A] flex items-center justify-center" data-testid="trial-success">
        <div className="max-w-lg mx-auto px-4 text-center">
          <div className="w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h2 className="font-heading text-3xl font-bold uppercase tracking-tight text-white">
            Kaydınız Alındı!
          </h2>
          <p className="mt-4 text-white/60 leading-relaxed">
            Deneme antrenman başvurunuz başarıyla alınmıştır. En kısa sürede sizinle iletişime geçilecektir.
          </p>
          <div className="mt-6 bg-white/5 border border-white/10 p-4">
            <p className="text-sm text-white/80">
              <strong className="text-gold">{form.trial_day}</strong> tarihinde saat <strong className="text-gold">{form.trial_time}</strong>'de görüşmek üzere!
            </p>
          </div>
          <div className="mt-6 bg-amber-500/10 border border-amber-500/30 p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-200 text-left">
              Antrenmana gelemeyecekseniz lütfen bizi önceden bilgilendiriniz.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 lg:pt-20 min-h-screen bg-[#1A1A1A]" data-testid="trial-registration-page">
      <SEOHead
        title="Ücretsiz Deneme Antrenmanı - İRÜ Spor Kulübü"
        description="İRÜ Spor Kulübü ücretsiz deneme antrenmanına katılın. Voleybol deneme antrenmanı kayıt formu."
        keywords="ücretsiz deneme antrenmanı, voleybol deneme, spor kulübü deneme, Ataşehir voleybol"
      />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 lg:py-20">
        {/* Header */}
        <div className="text-center mb-10">
          <span className="font-accent text-sm uppercase tracking-widest text-gold">Ücretsiz</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white mt-2">
            Deneme Antrenmanı Kayıt
          </h1>
          <p className="mt-3 text-base text-white/60 max-w-lg mx-auto">
            Aşağıdaki formu doldurarak ücretsiz deneme antrenmanımıza kayıt olun.
          </p>
          <p className="mt-2 inline-flex items-center gap-2 text-sm text-gold font-medium bg-gold/10 border border-gold/20 px-3 py-1.5">
            İlk antrenman ücretsizdir!
          </p>
        </div>

        {/* Warning */}
        <div className="bg-amber-500/10 border border-amber-500/30 p-4 flex items-start gap-3 mb-8">
          <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-200">
            Lütfen antrenmana gelemeyecekseniz bizi önceden bilgilendiriniz. Yerinizi bekleyen başka sporcularımız olabilir.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8" data-testid="trial-form">
          {/* Sporcu Bilgileri */}
          <div className="bg-white/5 border border-white/10 p-6">
            <div className="flex items-center gap-2 mb-5">
              <User className="w-5 h-5 text-gold" />
              <h2 className="font-heading text-base font-bold uppercase tracking-wider text-white">Sporcu Bilgileri</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-accent uppercase tracking-wider text-gray-400">Ad *</Label>
                <Input
                  value={form.athlete_name}
                  onChange={(e) => updateField("athlete_name", e.target.value)}
                  data-testid="trial-athlete-name"
                  className={`mt-1.5 bg-white/5 border-white/10 text-white rounded-none h-11 ${errors.athlete_name ? "border-red-500" : ""}`}
                  placeholder="Sporcu adı"
                />
              </div>
              <div>
                <Label className="text-xs font-accent uppercase tracking-wider text-gray-400">Soyad *</Label>
                <Input
                  value={form.athlete_surname}
                  onChange={(e) => updateField("athlete_surname", e.target.value)}
                  data-testid="trial-athlete-surname"
                  className={`mt-1.5 bg-white/5 border-white/10 text-white rounded-none h-11 ${errors.athlete_surname ? "border-red-500" : ""}`}
                  placeholder="Sporcu soyadı"
                />
              </div>
              <div>
                <Label className="text-xs font-accent uppercase tracking-wider text-gray-400">Doğum Tarihi *</Label>
                <Input
                  type="date"
                  value={form.birth_date}
                  onChange={(e) => updateField("birth_date", e.target.value)}
                  data-testid="trial-birth-date"
                  className={`mt-1.5 bg-white/5 border-white/10 text-white rounded-none h-11 ${errors.birth_date ? "border-red-500" : ""}`}
                />
              </div>
              <div>
                <Label className="text-xs font-accent uppercase tracking-wider text-gray-400">Daha önce lisansı çıktı mı? *</Label>
                <div className="flex gap-3 mt-2">
                  {["Evet", "Hayır"].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => updateField("has_license", opt)}
                      data-testid={`trial-license-${opt.toLowerCase()}`}
                      className={`flex-1 py-2.5 text-sm font-medium border transition-all ${
                        form.has_license === opt
                          ? "bg-burgundy border-burgundy text-white"
                          : `bg-white/5 border-white/10 text-white/60 hover:border-white/30 ${errors.has_license ? "border-red-500" : ""}`
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Veli Bilgileri */}
          <div className="bg-white/5 border border-white/10 p-6">
            <div className="flex items-center gap-2 mb-5">
              <Phone className="w-5 h-5 text-gold" />
              <h2 className="font-heading text-base font-bold uppercase tracking-wider text-white">Veli Bilgileri</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-accent uppercase tracking-wider text-gray-400">Ad *</Label>
                <Input
                  value={form.parent_name}
                  onChange={(e) => updateField("parent_name", e.target.value)}
                  data-testid="trial-parent-name"
                  className={`mt-1.5 bg-white/5 border-white/10 text-white rounded-none h-11 ${errors.parent_name ? "border-red-500" : ""}`}
                  placeholder="Veli adı"
                />
              </div>
              <div>
                <Label className="text-xs font-accent uppercase tracking-wider text-gray-400">Soyad *</Label>
                <Input
                  value={form.parent_surname}
                  onChange={(e) => updateField("parent_surname", e.target.value)}
                  data-testid="trial-parent-surname"
                  className={`mt-1.5 bg-white/5 border-white/10 text-white rounded-none h-11 ${errors.parent_surname ? "border-red-500" : ""}`}
                  placeholder="Veli soyadı"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs font-accent uppercase tracking-wider text-gray-400">Telefon Numarası *</Label>
                <Input
                  value={form.parent_phone}
                  onChange={(e) => updateField("parent_phone", e.target.value)}
                  data-testid="trial-parent-phone"
                  className={`mt-1.5 bg-white/5 border-white/10 text-white rounded-none h-11 ${errors.parent_phone ? "border-red-500" : ""}`}
                  placeholder="05XX XXX XX XX"
                />
              </div>
            </div>
          </div>

          {/* Antrenman Seçimi */}
          <div className="bg-white/5 border border-white/10 p-6">
            <div className="flex items-center gap-2 mb-5">
              <Calendar className="w-5 h-5 text-gold" />
              <h2 className="font-heading text-base font-bold uppercase tracking-wider text-white">Antrenman Günü ve Saati</h2>
            </div>

            {/* Day Selection */}
            <div className="mb-6">
              <Label className="text-xs font-accent uppercase tracking-wider text-gray-400">Gün Seçimi *</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                {dayOptions.map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => updateField("trial_day", day)}
                    data-testid={`trial-day-${day.replace(" ", "-").toLowerCase()}`}
                    className={`py-4 text-center border transition-all ${
                      form.trial_day === day
                        ? "bg-burgundy border-burgundy text-white"
                        : `bg-white/5 border-white/10 text-white/60 hover:border-white/30 ${errors.trial_day ? "border-red-500" : ""}`
                    }`}
                  >
                    <Calendar className="w-5 h-5 mx-auto mb-1.5 opacity-60" />
                    <p className="font-heading text-sm font-bold uppercase tracking-wider">{day}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Time Selection */}
            <div>
              <Label className="text-xs font-accent uppercase tracking-wider text-gray-400">Saat Seçimi *</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                {timeSlots.map((slot) => (
                  <button
                    key={slot.time}
                    type="button"
                    onClick={() => updateField("trial_time", slot.time)}
                    data-testid={`trial-time-${slot.time.replace(":", "")}`}
                    className={`p-4 text-left border transition-all ${
                      form.trial_time === slot.time
                        ? "bg-burgundy border-burgundy text-white"
                        : `bg-white/5 border-white/10 text-white/60 hover:border-white/30 ${errors.trial_time ? "border-red-500" : ""}`
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 opacity-60 shrink-0" />
                      <div>
                        <p className="font-heading text-lg font-bold">{slot.time}</p>
                        <p className="text-xs opacity-70 mt-0.5">{slot.label}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Consent */}
          <div className="bg-white/5 border border-white/10 p-6">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="trial-consent">
              <input
                type="checkbox"
                checked={form.consent}
                onChange={(e) => updateField("consent", e.target.checked)}
                className={`mt-1 w-5 h-5 rounded border-2 accent-burgundy ${errors.consent ? "border-red-500" : "border-white/30"}`}
              />
              <span className="text-sm text-white/70 leading-relaxed">
                Numaramın İstanbul Rumeli Üniversitesi Spor Kulübü'nün iletişim kurması amacıyla paylaşılacağını onaylıyorum. *
              </span>
            </label>
          </div>

          {/* Submit */}
          <Button
            type="submit"
            disabled={loading}
            data-testid="trial-submit-btn"
            className="w-full bg-burgundy hover:bg-burgundy-700 text-white font-heading uppercase tracking-wider rounded-none h-14 text-base"
          >
            {loading ? "Kaydediliyor..." : "Deneme Antrenmanına Kayıt Ol"}
          </Button>
        </form>
      </div>
    </div>
  );
}
